import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Settings,
  Logout,
  CheckCircle,
  CloudUpload,
  Save,
  RestartAlt,
  Refresh,
  Download,
  DeleteOutline,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useThemeContext } from '../contexts/ThemeContext';
import { loadAppConfig, saveAppConfig } from '../services/configService';
import packageJson from '../../package.json';

// Constant for determining when to use multiline text input
const MULTILINE_THRESHOLD = 50;

const SetupPage = () => {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();
  const { t, customTranslations, updateCustomTranslations, resetCustomTranslations, getDefaultTranslations, setCustomTranslationsLocal } = useLanguage();
  const { themeColors: contextThemeColors, updateThemeColors, resetThemeColors, setThemeColorsLocal } = useThemeContext();

  // State for configuration
  const [config, setConfig] = useState({
    // SharePoint configuration
    clientId: process.env.REACT_APP_CLIENT_ID || '',
    tenantId: process.env.REACT_APP_TENANT_ID || '',
    clientSecret: process.env.REACT_APP_CLIENT_SECRET || '',
    sharePointSiteUrl: process.env.REACT_APP_SHAREPOINT_SITE_URL || '',
    sharePointLibraryName: process.env.REACT_APP_SHAREPOINT_LIBRARY_NAME || '',
    sharePointSiteId: process.env.REACT_APP_SHAREPOINT_SITE_ID || '',
    sharePointListId: process.env.REACT_APP_SHAREPOINT_LIST_ID || '',
    // Admin credentials
    adminUsername: process.env.REACT_APP_ADMIN_USERNAME || '',
    adminPassword: process.env.REACT_APP_ADMIN_PASSWORD || '',
    // Dataverse configuration
    dataverseBaseUrl: process.env.REACT_APP_DATAVERSE_BASE_URL || '',
    dataverseEntitySet: process.env.REACT_APP_DATAVERSE_ENTITY_SET || '',
    dataverseScope: process.env.REACT_APP_DATAVERSE_SCOPE || '',
    // CORS proxy configuration
    corsProxy: process.env.REACT_APP_CORS_PROXY || '',
    // Backend API URL
    backendUrl: process.env.REACT_APP_BACKEND_URL || '',
  });

  // State for theme colors (local state for editing)
  const [themeColors, setThemeColors] = useState(contextThemeColors);

  // State for logo
  const [logoPreview, setLogoPreview] = useState(null);

  // State for translations
  const [editedTranslations, setEditedTranslations] = useState(() => {
    const defaults = getDefaultTranslations();
    return {
      en: { ...defaults.en, ...customTranslations.en },
      it: { ...defaults.it, ...customTranslations.it },
    };
  });

  // State for text editing tab
  const [textEditTab, setTextEditTab] = useState(0);

  // State for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Features list
  const features = [
    { key: 'search', text: t('featureSearch') || 'Document search by serial number' },
    { key: 'download', text: t('featureDownload') || 'Direct download from SharePoint' },
    { key: 'oauth', text: t('featureOAuth') || 'OAuth authentication with Microsoft' },
    { key: 'responsive', text: t('featureResponsive') || 'Responsive mobile-first design' },
    { key: 'multilang', text: t('featureMultilang') || 'Multi-language support (IT/EN)' },
    { key: 'material', text: t('featureMaterial') || 'Modern Material Design UI' },
  ];

  const handleConfigChange = (field) => (event) => {
    setConfig({
      ...config,
      [field]: event.target.value,
    });
  };

  const handleColorChange = (field) => (event) => {
    setThemeColors({
      ...themeColors,
      [field]: event.target.value,
    });
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setSnackbar({
          open: true,
          message: t('logoTooLarge') || 'Logo file is too large (max 2MB)',
          severity: 'error',
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = async () => {
        setLogoPreview(reader.result);
        
        // Save to API
        try {
          const config = await loadAppConfig();
          const updatedConfig = {
            ...config,
            logo: reader.result,
          };
          await saveAppConfig(updatedConfig);
        } catch (error) {
          console.error('Failed to save logo to API:', error);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTranslationChange = (lang, key) => (event) => {
    setEditedTranslations({
      ...editedTranslations,
      [lang]: {
        ...editedTranslations[lang],
        [key]: event.target.value,
      },
    });
  };

  const handleSaveTranslations = async () => {
    // Only save translations that differ from defaults
    const defaults = getDefaultTranslations();
    const customOnly = {
      en: {},
      it: {},
    };
    
    Object.keys(editedTranslations.en).forEach(key => {
      if (editedTranslations.en[key] !== defaults.en[key]) {
        customOnly.en[key] = editedTranslations.en[key];
      }
    });
    
    Object.keys(editedTranslations.it).forEach(key => {
      if (editedTranslations.it[key] !== defaults.it[key]) {
        customOnly.it[key] = editedTranslations.it[key];
      }
    });
    
    await updateCustomTranslations(customOnly);
    
    setSnackbar({
      open: true,
      message: t('configSavedRuntime') || 'Configuration saved! Click "Apply and Reload" to apply changes.',
      severity: 'success',
    });
  };

  const handleResetTranslations = async () => {
    const defaults = getDefaultTranslations();
    setEditedTranslations({
      en: { ...defaults.en },
      it: { ...defaults.it },
    });
    await resetCustomTranslations();
    setSnackbar({
      open: true,
      message: t('textsReset') || 'Texts reset to default',
      severity: 'success',
    });
  };

  const handleSaveConfiguration = async () => {
    try {
      // Build custom translations (only changed keys)
      const defaults = getDefaultTranslations();
      const customOnly = { en: {}, it: {} };
      Object.keys(editedTranslations.en).forEach(key => {
        if (editedTranslations.en[key] !== defaults.en[key]) {
          customOnly.en[key] = editedTranslations.en[key];
        }
      });
      Object.keys(editedTranslations.it).forEach(key => {
        if (editedTranslations.it[key] !== defaults.it[key]) {
          customOnly.it[key] = editedTranslations.it[key];
        }
      });

      // Load current config once, merge everything, save once
      const apiConfig = await loadAppConfig() || {};
      const updatedConfig = {
        ...apiConfig,
        logo: apiConfig.logo || '',
        theme: themeColors,
        translations: customOnly,
        settings: {
          clientId: config.clientId,
          tenantId: config.tenantId,
          clientSecret: config.clientSecret,
          sharePointSiteUrl: config.sharePointSiteUrl,
          sharePointLibraryName: config.sharePointLibraryName,
          sharePointSiteId: config.sharePointSiteId,
          sharePointListId: config.sharePointListId,
          dataverseBaseUrl: config.dataverseBaseUrl,
          dataverseEntitySet: config.dataverseEntitySet,
          dataverseScope: config.dataverseScope,
          corsProxy: config.corsProxy,
          backendUrl: config.backendUrl,
          adminUsername: config.adminUsername,
          adminPassword: config.adminPassword,
        },
      };

      const saved = await saveAppConfig(updatedConfig);

      // Update local state in contexts (no extra API calls)
      if (saved) {
        setThemeColorsLocal(themeColors);
        setCustomTranslationsLocal(customOnly);
      }

      setSnackbar({
        open: true,
        message: saved
          ? (t('configSavedRuntime') || 'Configuration saved! Click "Apply and Reload" to apply changes.')
          : 'Configuration saved locally. Backend API not available.',
        severity: saved ? 'success' : 'warning',
      });
    } catch (error) {
      console.error('Failed to save configuration:', error);
      setSnackbar({
        open: true,
        message: 'Error while saving configuration',
        severity: 'error',
      });
    }
  };

  const handleApplyAndReload = () => {
    handleSaveConfiguration();
    // Wait a bit for the API to save, then reload
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleExportEnvFile = () => {
    // Generate .env file content for download
    const envContent = `# SharePoint OAuth Configuration
# Copy this file to .env and fill in your values

# Azure AD Application (client) ID
REACT_APP_CLIENT_ID=${config.clientId}

# Azure AD Directory (tenant) ID
REACT_APP_TENANT_ID=${config.tenantId}

# Azure AD Client Secret (for client credentials flow)
# IMPORTANT: Keep this secret secure and never commit it to source control
REACT_APP_CLIENT_SECRET=${config.clientSecret}

# SharePoint site URL (e.g., https://your-tenant.sharepoint.com/sites/your-site)
REACT_APP_SHAREPOINT_SITE_URL=${config.sharePointSiteUrl}

# SharePoint document library name (default: Documents)
REACT_APP_SHAREPOINT_LIBRARY_NAME=${config.sharePointLibraryName}

# SharePoint site ID (format: tenant.sharepoint.com,site-guid)
REACT_APP_SHAREPOINT_SITE_ID=${config.sharePointSiteId}

# SharePoint list ID (GUID of the document library list)
REACT_APP_SHAREPOINT_LIST_ID=${config.sharePointListId}

# Admin credentials for setup page
# IMPORTANT: Change these default values for security
REACT_APP_ADMIN_USERNAME=${config.adminUsername}
REACT_APP_ADMIN_PASSWORD=${config.adminPassword}

# Dataverse Configuration for querying matricola data
# Base URL for Dataverse API
REACT_APP_DATAVERSE_BASE_URL=${config.dataverseBaseUrl}

# Entity set name in Dataverse
REACT_APP_DATAVERSE_ENTITY_SET=${config.dataverseEntitySet}

# Dataverse OAuth scope
REACT_APP_DATAVERSE_SCOPE=${config.dataverseScope}

# CORS Proxy Configuration
# CORS proxy URL for API calls (e.g., https://corsproxy.io/? or https://api.allorigins.win/raw?url=)
# Leave empty for direct calls
REACT_APP_CORS_PROXY=${config.corsProxy}

# Backend API Configuration
# Backend API URL (e.g., https://fireco-app.azurewebsites.net)
# Leave empty to use CORS proxy
REACT_APP_BACKEND_URL=${config.backendUrl}
`;
    
    // Create downloadable .env file
    const blob = new Blob([envContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = '.env';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    setSnackbar({
      open: true,
      message: t('envFileExported') || 'File .env esportato con successo!',
      severity: 'success',
    });
  };

  const handleResetConfiguration = async () => {
    await resetCustomTranslations();
    const defaults = getDefaultTranslations();
    setEditedTranslations({
      en: { ...defaults.en },
      it: { ...defaults.it },
    });
    
    // Reset API configuration
    try {
      const defaultConfig = {
        logo: '',
        theme: {
          topBarBackground: '#bf0404',
          topBarText: '#ffffff',
          bodyBackground: '#323130',
          bodyPaper: '#3a3938',
          bodyPrimaryButton: '#bf0404',
          bodyPrimaryButtonHover: '#9a0303',
          bodyPrimaryButtonText: '#ffffff',
          textPrimary: '#ffffff',
          textSecondary: '#b3b3b3',
          footerBackground: '#3a3938',
          footerText: '#b3b3b3'
        },
        translations: { en: {}, it: {} },
        settings: {
          clientId: '',
          tenantId: '',
          clientSecret: '',
          sharePointSiteUrl: '',
          sharePointLibraryName: '',
          sharePointSiteId: '',
          sharePointListId: '',
          dataverseBaseUrl: '',
          dataverseEntitySet: '',
          dataverseScope: '',
          corsProxy: '',
          backendUrl: '',
          adminUsername: 'admin',
          adminPassword: 'admin123'
        }
      };
      await saveAppConfig(defaultConfig);
    } catch (error) {
      console.error('Failed to reset API configuration:', error);
    }
    
    setSnackbar({
      open: true,
      message: t('configReset') || 'Configuration reset to .env values. Reload the page to apply.',
      severity: 'success',
    });
  };

  const handleResetColors = async () => {
    const defaultColors = {
      // Top Bar (Barra Superiore)
      topBarBackground: '#bf0404',
      topBarText: '#ffffff',
      // Body (Corpo)
      bodyBackground: '#323130',
      bodyPaper: '#3a3938',
      bodyPrimaryButton: '#bf0404',
      bodyPrimaryButtonHover: '#9a0303',
      bodyPrimaryButtonText: '#ffffff',
      // Text Colors (Colori Testo)
      textPrimary: '#ffffff',
      textSecondary: '#b3b3b3',
      // Footer (Footer inferiore)
      footerBackground: '#3a3938',
      footerText: '#b3b3b3', // Approximation of rgba(255, 255, 255, 0.7)
    };
    setThemeColors(defaultColors);
    await resetThemeColors();
    setSnackbar({
      open: true,
      message: t('colorsReset') || 'Colors reset to default',
      severity: 'success',
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Load saved colors on context theme change
  useEffect(() => {
    setThemeColors(contextThemeColors);
  }, [contextThemeColors]);

  // Load saved logo and configuration on mount
  useEffect(() => {
    const loadConfigFromAPI = async () => {
      // Load configuration from API
      const apiConfig = await loadAppConfig();
      
      // Load logo
      if (apiConfig && apiConfig.logo) {
        setLogoPreview(apiConfig.logo);
      }
      
      // Load settings from API
      if (apiConfig && apiConfig.settings) {
        const loadedSettings = apiConfig.settings;
        setConfig({
          clientId: loadedSettings.clientId || process.env.REACT_APP_CLIENT_ID || '',
          tenantId: loadedSettings.tenantId || process.env.REACT_APP_TENANT_ID || '',
          clientSecret: loadedSettings.clientSecret || process.env.REACT_APP_CLIENT_SECRET || '',
          sharePointSiteUrl: loadedSettings.sharePointSiteUrl || process.env.REACT_APP_SHAREPOINT_SITE_URL || '',
          sharePointLibraryName: loadedSettings.sharePointLibraryName || process.env.REACT_APP_SHAREPOINT_LIBRARY_NAME || '',
          sharePointSiteId: loadedSettings.sharePointSiteId || process.env.REACT_APP_SHAREPOINT_SITE_ID || '',
          sharePointListId: loadedSettings.sharePointListId || process.env.REACT_APP_SHAREPOINT_LIST_ID || '',
          adminUsername: loadedSettings.adminUsername || process.env.REACT_APP_ADMIN_USERNAME || '',
          adminPassword: loadedSettings.adminPassword || process.env.REACT_APP_ADMIN_PASSWORD || '',
          dataverseBaseUrl: loadedSettings.dataverseBaseUrl || process.env.REACT_APP_DATAVERSE_BASE_URL || '',
          dataverseEntitySet: loadedSettings.dataverseEntitySet || process.env.REACT_APP_DATAVERSE_ENTITY_SET || '',
          dataverseScope: loadedSettings.dataverseScope || process.env.REACT_APP_DATAVERSE_SCOPE || '',
          corsProxy: loadedSettings.corsProxy || process.env.REACT_APP_CORS_PROXY || '',
          backendUrl: loadedSettings.backendUrl || process.env.REACT_APP_BACKEND_URL || '',
        });
      }
    };
    
    loadConfigFromAPI();
  }, []); // Empty dependency array - only load once on mount

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <Settings sx={{ mr: 2 }} />
          <Typography variant="h6" component="h1" sx={{ flexGrow: 1 }}>
            {t('setupPageTitle') || 'Admin Setup'}
          </Typography>
          <IconButton color="inherit" onClick={handleLogout} title="Logout">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ flexGrow: 1, py: 4 }}>
        <Grid container spacing={3}>
          {/* Version and Features */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('currentVersion') || 'Current Version'}
              </Typography>
              <Typography variant="h4" color="primary" sx={{ mb: 2 }}>
                v{packageJson.version}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>
                {t('features') || 'Features'}
              </Typography>
              <List>
                {features.map((feature) => (
                  <ListItem key={feature.key}>
                    <ListItemIcon>
                      <CheckCircle color="success" />
                    </ListItemIcon>
                    <ListItemText primary={feature.text} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Theme Colors */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('themeColors') || 'Theme Colors'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<RestartAlt />}
                  onClick={handleResetColors}
                >
                  {t('reset') || 'Reset'}
                </Button>
              </Box>
              
              {/* Top Bar Section */}
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
                {t('topBarSection') || 'Barra Superiore (Top Bar)'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.topBarBackground,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('topBarBackground') || 'Sfondo Barra'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.topBarBackground}
                    onChange={handleColorChange('topBarBackground')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('topBarBackgroundDescription') || 'Background color of the navigation bar at the top'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.topBarText,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('topBarText') || 'Testo Barra'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.topBarText}
                    onChange={handleColorChange('topBarText')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('topBarTextDescription') || 'Text color in the navigation bar'}
                  />
                </Grid>
              </Grid>

              {/* Body Section */}
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                {t('bodySection') || 'Corpo (Body)'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.bodyBackground,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('bodyBackground') || 'Sfondo Corpo'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.bodyBackground}
                    onChange={handleColorChange('bodyBackground')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('bodyBackgroundDescription') || 'Main background color of the page'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.bodyPaper,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('bodyPaper') || 'Colore Carta'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.bodyPaper}
                    onChange={handleColorChange('bodyPaper')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('bodyPaperDescription') || 'Background color for content cards and panels'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.bodyPrimaryButton,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('bodyPrimaryButton') || 'Pulsante Primario'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.bodyPrimaryButton}
                    onChange={handleColorChange('bodyPrimaryButton')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('bodyPrimaryButtonDescription') || 'Color of primary action buttons'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.bodyPrimaryButtonHover,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('bodyPrimaryButtonHover') || 'Pulsante Hover'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.bodyPrimaryButtonHover}
                    onChange={handleColorChange('bodyPrimaryButtonHover')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('bodyPrimaryButtonHoverDescription') || 'Color when hovering over primary buttons'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.bodyPrimaryButtonText,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('bodyPrimaryButtonText') || 'Testo Pulsante Primario'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.bodyPrimaryButtonText}
                    onChange={handleColorChange('bodyPrimaryButtonText')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('bodyPrimaryButtonTextDescription') || 'Text color on primary buttons'}
                  />
                </Grid>
              </Grid>

              {/* Text Colors Section */}
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                {t('textColorsSection') || 'Colori Testo (Text Colors)'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.textPrimary,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('textPrimary') || 'Testo Primario'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.textPrimary}
                    onChange={handleColorChange('textPrimary')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('textPrimaryDescription') || 'Main text color throughout the application'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.textSecondary,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('textSecondary') || 'Testo Secondario'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.textSecondary}
                    onChange={handleColorChange('textSecondary')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('textSecondaryDescription') || 'Color for secondary or helper text'}
                  />
                </Grid>
              </Grid>

              {/* Footer Section */}
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
                {t('footerSection') || 'Footer Inferiore (Footer)'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.footerBackground,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('footerBackground') || 'Sfondo Footer'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.footerBackground}
                    onChange={handleColorChange('footerBackground')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('footerBackgroundDescription') || 'Background color of the page footer'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: themeColors.footerText,
                        border: '2px solid',
                        borderColor: 'divider',
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" fontWeight="medium">
                      {t('footerTextColor') || 'Colore Testo Footer'}
                    </Typography>
                  </Box>
                  <TextField
                    fullWidth
                    type="color"
                    value={themeColors.footerText}
                    onChange={handleColorChange('footerText')}
                    InputLabelProps={{ shrink: true }}
                    helperText={t('footerTextColorDescription') || 'Text color in the footer area'}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Logo Upload */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('logoUpload') || 'Logo Upload'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('logoUploadDescription') || 'Upload a logo for the top bar (max 2MB, PNG/JPG)'}
              </Typography>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                fullWidth
                sx={{ mb: 2 }}
              >
                {t('uploadLogo') || 'Upload Logo'}
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/jpg"
                  onChange={handleLogoUpload}
                />
              </Button>
              {logoPreview && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    textAlign: 'center',
                  }}
                >
                  <img
                    src={logoPreview}
                    alt="Logo preview"
                    style={{ maxWidth: '100%', maxHeight: '100px' }}
                  />
                </Box>
              )}
            </Paper>

            {/* Text Customization */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('textsSection') || 'Text Customization'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<RestartAlt />}
                  onClick={handleResetTranslations}
                >
                  {t('textsResetButton') || 'Reset Texts'}
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('textsDescription') || 'Customize the text displayed in the application for both languages'}
              </Typography>
              
              <Tabs value={textEditTab} onChange={(e, newValue) => setTextEditTab(newValue)} sx={{ mb: 2 }}>
                <Tab label={t('languageTabIT') || 'Italian'} />
                <Tab label={t('languageTabEN') || 'English'} />
              </Tabs>

              <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
                {textEditTab === 0 && (
                  <Grid container spacing={2}>
                    {Object.keys(editedTranslations.it).map((key) => (
                      <Grid item xs={12} key={key}>
                        <TextField
                          fullWidth
                          label={key}
                          value={editedTranslations.it[key]}
                          onChange={handleTranslationChange('it', key)}
                          variant="outlined"
                          size="small"
                          multiline={editedTranslations.it[key] && editedTranslations.it[key].length > MULTILINE_THRESHOLD}
                          rows={editedTranslations.it[key] && editedTranslations.it[key].length > MULTILINE_THRESHOLD ? 2 : 1}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
                {textEditTab === 1 && (
                  <Grid container spacing={2}>
                    {Object.keys(editedTranslations.en).map((key) => (
                      <Grid item xs={12} key={key}>
                        <TextField
                          fullWidth
                          label={key}
                          value={editedTranslations.en[key]}
                          onChange={handleTranslationChange('en', key)}
                          variant="outlined"
                          size="small"
                          multiline={editedTranslations.en[key] && editedTranslations.en[key].length > MULTILINE_THRESHOLD}
                          rows={editedTranslations.en[key] && editedTranslations.en[key].length > MULTILINE_THRESHOLD ? 2 : 1}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>

              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveTranslations}
                >
                  {t('textsSaveButton') || 'Save Texts'}
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Environment Variables Configuration */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('environmentVariables') || 'Environment Variables'}
              </Typography>
              <Alert severity="success" sx={{ mb: 2 }}>
                {t('envVarWarningRuntime') || 'Configuration changes are saved in the browser and applied immediately after reloading. No need to modify the .env file.'}
              </Alert>
              <Alert severity="warning" sx={{ mb: 3 }}>
                {t('envSecurityWarning') || '⚠️ SECURITY WARNING: Credentials are stored in the browser. Do not share this device with unauthorized people.'}
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_CLIENT_ID"
                    value={config.clientId}
                    onChange={handleConfigChange('clientId')}
                    margin="normal"
                    helperText={t('clientIdHelp') || 'Azure AD Application (client) ID'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_TENANT_ID"
                    value={config.tenantId}
                    onChange={handleConfigChange('tenantId')}
                    margin="normal"
                    helperText={t('tenantIdHelp') || 'Azure AD Directory (tenant) ID'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_CLIENT_SECRET"
                    type="password"
                    value={config.clientSecret}
                    onChange={handleConfigChange('clientSecret')}
                    margin="normal"
                    helperText={t('clientSecretHelp') || 'Azure AD Client Secret'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_SHAREPOINT_SITE_URL"
                    value={config.sharePointSiteUrl}
                    onChange={handleConfigChange('sharePointSiteUrl')}
                    margin="normal"
                    helperText={t('siteUrlHelp') || 'SharePoint site URL'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_SHAREPOINT_LIBRARY_NAME"
                    value={config.sharePointLibraryName}
                    onChange={handleConfigChange('sharePointLibraryName')}
                    margin="normal"
                    helperText={t('libraryNameHelp') || 'SharePoint document library name'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_SHAREPOINT_SITE_ID"
                    value={config.sharePointSiteId}
                    onChange={handleConfigChange('sharePointSiteId')}
                    margin="normal"
                    helperText={t('siteIdHelp') || 'SharePoint site ID'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_SHAREPOINT_LIST_ID"
                    value={config.sharePointListId}
                    onChange={handleConfigChange('sharePointListId')}
                    margin="normal"
                    helperText={t('listIdHelp') || 'SharePoint list ID (GUID)'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_ADMIN_USERNAME"
                    value={config.adminUsername}
                    onChange={handleConfigChange('adminUsername')}
                    margin="normal"
                    helperText={t('adminUsernameHelp') || 'Admin username for setup page'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_ADMIN_PASSWORD"
                    type="password"
                    value={config.adminPassword}
                    onChange={handleConfigChange('adminPassword')}
                    margin="normal"
                    helperText={t('adminPasswordHelp') || 'Admin password for setup page'}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                {t('dataverseConfiguration') || 'Dataverse Configuration'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_DATAVERSE_BASE_URL"
                    value={config.dataverseBaseUrl}
                    onChange={handleConfigChange('dataverseBaseUrl')}
                    margin="normal"
                    helperText={t('dataverseBaseUrlHelp') || 'Base URL of Dataverse (e.g.: https://orgc0465cb6.crm4.dynamics.com)'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_DATAVERSE_ENTITY_SET"
                    value={config.dataverseEntitySet}
                    onChange={handleConfigChange('dataverseEntitySet')}
                    margin="normal"
                    helperText={t('dataverseEntitySetHelp') || 'Name of the Dataverse entity set for serial number search'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_DATAVERSE_SCOPE"
                    value={config.dataverseScope}
                    onChange={handleConfigChange('dataverseScope')}
                    margin="normal"
                    helperText={t('dataverseScopeHelp') || 'OAuth scope for Dataverse (e.g.: https://orgc0465cb6.crm4.dynamics.com/.default)'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_CORS_PROXY"
                    value={config.corsProxy}
                    onChange={handleConfigChange('corsProxy')}
                    margin="normal"
                    helperText={t('corsProxyUrlHelp')}
                    placeholder="https://corsproxy.io/?"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="REACT_APP_BACKEND_URL"
                    value={config.backendUrl}
                    onChange={handleConfigChange('backendUrl')}
                    margin="normal"
                    helperText={t('backendApiUrlHelp')}
                    placeholder="https://fireco-app.azurewebsites.net"
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Download />}
                    onClick={handleExportEnvFile}
                  >
                    {t('exportEnvFile') || 'Esporta .env'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteOutline />}
                    onClick={handleResetConfiguration}
                  >
                    {t('resetConfig') || 'Reset'}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<Save />}
                    onClick={handleSaveConfiguration}
                    size="large"
                  >
                    {t('saveConfiguration') || 'Save Configuration'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Refresh />}
                    onClick={handleApplyAndReload}
                    size="large"
                  >
                    {t('applyAndReload') || 'Apply and Reload'}
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetupPage;
